import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';
import { buttonVariants } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';

type LoadState = 'loading' | 'loaded' | 'error';

function canRenderInlineImage(mimeType: string | null): boolean {
  if (!mimeType) return true;
  return mimeType.startsWith('image/');
}

interface InstapayReceiptInlinePreviewProps {
  previewUrl: string;
  fileName: string | null;
  mimeType: string | null;
}

function PreviewLoadingSpinner() {
  return (
    <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
      />
    </svg>
  );
}

function OpenPreviewLink({ previewUrl, label }: { previewUrl: string; label: string }) {
  return (
    <a
      href={previewUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'inline-flex')}
    >
      <Icons.Eye className="h-4 w-4" />
      {label}
    </a>
  );
}

export function InstapayReceiptInlinePreview({
  previewUrl,
  fileName,
  mimeType,
}: InstapayReceiptInlinePreviewProps) {
  const { t } = useTranslation();
  const [loadState, setLoadState] = useState<LoadState>(() =>
    canRenderInlineImage(mimeType) ? 'loading' : 'error',
  );

  useEffect(() => {
    setLoadState(canRenderInlineImage(mimeType) ? 'loading' : 'error');
  }, [previewUrl, mimeType]);

  const alt = fileName ?? t('payments.instapay.receipt.previewAlt');
  const openLabel = t('payments.instapay.receipt.openPreview');

  if (loadState === 'error') {
    return (
      <div className="flex flex-col items-start gap-3 rounded-lg border border-dashed border-border bg-muted/20 p-4">
        <p className="text-sm text-muted-foreground">
          {t('payments.instapay.receipt.previewLoadFailed')}
        </p>
        <OpenPreviewLink previewUrl={previewUrl} label={openLabel} />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-muted/30">
      <div className="relative flex min-h-48 w-full items-center justify-center">
        {loadState === 'loading' ? (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-sm text-muted-foreground"
            aria-live="polite"
            aria-busy="true"
          >
            <PreviewLoadingSpinner />
            <span>{t('payments.instapay.receipt.previewLoading')}</span>
          </div>
        ) : null}
        <img
          src={previewUrl}
          alt={alt}
          referrerPolicy="no-referrer"
          decoding="async"
          onLoad={() => setLoadState('loaded')}
          onError={() => setLoadState('error')}
          className={cn(
            'max-h-64 w-full object-contain',
            loadState === 'loading' && 'invisible absolute inset-0 h-full w-full',
          )}
        />
      </div>
    </div>
  );
}
